import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { IconHierarchy3 } from '@tabler/icons-react'

import { setSelectedNode, setDrawerActiveTab } from '@/reducers/voiceAgents/voiceAgents.redux'
import { getSelectedAgent } from '@/reducers/voiceAgents/voiceAgents.helpers'

import {
  computeAgentLayout,
  computeWorldSize,
  getNodePosition,
  getOverlayPeerIds,
  stripToolNodes,
} from './canvasHelpers'
import { useCanvasPanZoom } from './hooks/useCanvasPanZoom'
import { useNodeDrag } from './hooks/useNodeDrag'

import CanvasNode from './components/CanvasNode'
import CanvasEdges from './components/CanvasEdges'
import CanvasHint from './components/CanvasHint'
import CanvasControls from './components/CanvasControls'
import EmptyState from './components/EmptyState'

// =============================================================================
// CanvasView — call-flow graph composer. Reads the graph from the voiceAgents
// slice and composes two interaction hooks: useCanvasPanZoom (zoom/pan/wheel/
// fit) and useNodeDrag (node positions + drag). This component is render +
// composition; the interaction math/wiring lives in the hooks + canvasHelpers.
// Selecting a node dispatches setSelectedNode, which the drawer consumes.
// =============================================================================

const CanvasView = () => {
  const dispatch = useDispatch()
  const rawAgent = useSelector((state) => getSelectedAgent(state.voiceAgents))
  const selectedNodeId = useSelector((state) => state.voiceAgents.selectedNodeId)
  const agentConfigMissing = useSelector((state) => state.voiceAgents.agentConfigMissing)

  // Tool nodes render as chips inside the agents that call them, not as graph
  // nodes — drop them (and edges touching them) from the rendered copy only.
  const agent = useMemo(() => stripToolNodes(rawAgent), [rawAgent])

  const canvasElementRef = useRef(null)
  const panZoom = useCanvasPanZoom({ canvasElementRef })
  const drag = useNodeDrag({ zoomRef: panZoom.zoomRef })
  const {
    nodePositions,
    setNodePositions,
    handleNodePointerDown,
    consumeClickSuppression,
    snapshotDefaults,
    resetToDefaults,
  } = drag

  // Snapshot default positions whenever the agent changes (powers reset-layout)
  // and auto-fit the new graph so users never land on a clipped/off-screen view.
  useEffect(() => {
    if (!agent || !Array.isArray(agent.nodes)) {
      snapshotDefaults({})
      setNodePositions({})
      return undefined
    }
    // Smart layered layout first; fall back to an index-based row for any node
    // it doesn't place so we never stack nodes on the origin.
    const layoutPositions = computeAgentLayout(agent)
    const next = {}
    agent.nodes.forEach((node, index) => {
      next[node.id] = layoutPositions[node.id] || getNodePosition(node, index)
    })
    snapshotDefaults(next)
    setNodePositions(next)
    panZoom.resetView()
    // Defer the fit to the next frame so the canvas DOM has its final size.
    const raf = window.requestAnimationFrame(() => panZoom.fitTo(next))
    return () => window.cancelAnimationFrame(raf)
  }, [agent?.id])

  // World size = bounding box of all nodes + a margin. Drives the explicit
  // width/height of canvas-inner so its layout box contains the absolutely-
  // positioned nodes and the SVG covers their full extent.
  const worldSize = useMemo(() => computeWorldSize(nodePositions), [nodePositions])

  // Peer-handoff overlay: only when the selected node is a sub-agent, and only
  // for peers not already connected by a primary edge.
  const peerEdges = useMemo(() => {
    if (!agent || !selectedNodeId) {
      return []
    }
    const node = agent.nodes?.find((entry) => entry.id === selectedNodeId)
    if (!node) {
      return []
    }
    return getOverlayPeerIds(node, agent).map((peerId) => ({
      from: node.id,
      to: peerId,
      edgeCategory: 'peer-handoff',
    }))
  }, [agent, selectedNodeId])

  const handleNodeClick = useCallback(
    (nodeId) => {
      // A drag just ended → swallow the click so we don't re-select the node.
      if (consumeClickSuppression()) {
        return
      }
      dispatch(setSelectedNode(nodeId))
    },
    [dispatch, consumeClickSuppression]
  )

  // Chip-click: selects the node AND opens the matching drawer tab.
  // SET_SELECTED_NODE resets activeTab to 'prompt' first, so dispatch the tab
  // after the selection lands. Skip the redundant select when already selected
  // (that action also clears drawer edit/peek state a chip click shouldn't
  // disturb). Wrapped in useCallback so CanvasNode's React.memo holds.
  const selectedNodeIdRef = useRef(selectedNodeId)
  useEffect(() => {
    selectedNodeIdRef.current = selectedNodeId
  }, [selectedNodeId])
  const handleChipClick = useCallback(
    (nodeId, tabKey) => {
      if (selectedNodeIdRef.current !== nodeId) {
        dispatch(setSelectedNode(nodeId))
      }
      dispatch(setDrawerActiveTab(tabKey))
    },
    [dispatch]
  )

  if (!agent) {
    return (
      <div
        className="voice-agents__canvas voice-agents__canvas--empty"
        data-testid="canvas"
        aria-label="Call flow graph"
      >
        <EmptyState
          icon={IconHierarchy3}
          title={agentConfigMissing ? 'No prompts externalized yet' : 'No call flow yet'}
          hint={
            agentConfigMissing
              ? "This agent's call flow will appear here once its prompts are externalized."
              : 'Pick an agent to render its call flow — sub-agents, prompts, tools, and the edges that connect them.'
          }
          dataTestId="canvas-empty"
        />
      </div>
    )
  }

  return (
    <div
      ref={canvasElementRef}
      className="voice-agents__canvas"
      data-testid="canvas"
      aria-label="Call flow graph"
      onMouseDown={panZoom.handleCanvasMouseDown}
      onWheel={panZoom.handleWheel}
    >
      <CanvasHint />

      <div
        className="voice-agents__canvas-inner"
        data-testid="canvas-inner"
        style={{
          transform: `translate(${panZoom.pan.x}px, ${panZoom.pan.y}px) scale(${panZoom.zoom})`,
          width: `${worldSize.width}px`,
          height: `${worldSize.height}px`,
        }}
      >
        <CanvasEdges
          agent={agent}
          positionsByNodeId={nodePositions}
          selectedNodeId={selectedNodeId}
          peerEdges={peerEdges}
        />
        {agent.nodes?.map((node, index) => {
          const position = nodePositions[node.id] || getNodePosition(node, index)
          return (
            <CanvasNode
              key={node.id}
              nodeId={node.id}
              node={node}
              position={position}
              selected={selectedNodeId === node.id}
              onPointerDown={handleNodePointerDown}
              onClick={handleNodeClick}
              onChipClick={handleChipClick}
            />
          )
        })}
      </div>

      <CanvasControls
        zoom={panZoom.zoom}
        onZoomIn={panZoom.handleZoomIn}
        onZoomOut={panZoom.handleZoomOut}
        onResetZoom={panZoom.handleResetZoom}
        onFitToView={() => panZoom.fitTo(nodePositions)}
        onResetLayout={() => panZoom.fitTo(resetToDefaults())}
      />
    </div>
  )
}

export default CanvasView
